// Stop a half-filled form disappearing to a stray click.
//
// Two exits to cover. The browser's own — reload, close, back — is the standard
// `beforeunload`, which can only ask for the browser's generic confirm. The
// in-app one is a link in the sidebar or the breadcrumb, which never touches
// `beforeunload` because wouter navigates by pushState. So links are caught in
// the capture phase before the router sees them, and the destination is handed
// back for the caller to confirm with an AlertDialog.
//
// Only plain left-clicks on same-origin links are intercepted: a middle-click or
// a Cmd-click opens a new tab and leaves this form exactly where it was.

import { useCallback, useEffect, useRef, useState } from "react";

export interface LeaveGuard {
	/** Where a blocked navigation was heading, or null when nothing is blocked. */
	pendingHref: string | null;
	/** Stay put. */
	cancel: () => void;
	/** Leave for `pendingHref`, guard disarmed. */
	confirm: (navigate: (href: string) => void) => void;
	/** Route a button (Cancel, say) through the same check. */
	request: (href: string, navigate: (href: string) => void) => void;
	/**
	 * Disarm for good. For the case where the work has been handed to the
	 * server: the form is still dirty, but there is nothing left to lose.
	 */
	release: () => void;
}

export function useLeaveGuard(armed: boolean): LeaveGuard {
	const [pendingHref, setPendingHref] = useState<string | null>(null);
	// Set the moment a leave is agreed to, so the listeners below stop blocking
	// before React has re-rendered with `armed` false.
	const leaving = useRef(false);

	useEffect(() => {
		if (!armed) return;
		const onBeforeUnload = (event: BeforeUnloadEvent) => {
			if (leaving.current) return;
			event.preventDefault();
		};
		window.addEventListener("beforeunload", onBeforeUnload);
		return () => window.removeEventListener("beforeunload", onBeforeUnload);
	}, [armed]);

	useEffect(() => {
		if (!armed) return;
		const onClick = (event: MouseEvent) => {
			if (leaving.current || event.defaultPrevented) return;
			if (event.button !== 0) return;
			if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
				return;
			}
			const target = event.target;
			if (!(target instanceof Element)) return;
			const anchor = target.closest("a[href]");
			if (!(anchor instanceof HTMLAnchorElement)) return;
			if (anchor.hasAttribute("download")) return;
			if (anchor.target && anchor.target !== "_self") return;

			const url = new URL(anchor.href, window.location.href);
			if (url.origin !== window.location.origin) return;
			const href = `${url.pathname}${url.search}${url.hash}`;
			if (href === `${window.location.pathname}${window.location.search}`) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
			setPendingHref(href);
		};
		document.addEventListener("click", onClick, true);
		return () => document.removeEventListener("click", onClick, true);
	}, [armed]);

	const cancel = useCallback(() => setPendingHref(null), []);

	const confirm = useCallback(
		(navigate: (href: string) => void) => {
			if (!pendingHref) return;
			leaving.current = true;
			setPendingHref(null);
			navigate(pendingHref);
		},
		[pendingHref],
	);

	const request = useCallback(
		(href: string, navigate: (href: string) => void) => {
			if (!armed || leaving.current) {
				leaving.current = true;
				navigate(href);
				return;
			}
			setPendingHref(href);
		},
		[armed],
	);

	const release = useCallback(() => {
		leaving.current = true;
	}, []);

	return { pendingHref, cancel, confirm, request, release };
}
